// Hook badge counter sidebar — generic, configurable endpoint map.
// Pattern (port from attendance-app/admin/src/lib/sidebarBadges.js 2026-05-24):
//   1. Poll 30s (safety net cross-user change)
//   2. Refresh khi tab focus lại
//   3. Refresh INSTANT khi user action (component khác gọi triggerSidebarBadgeRefresh)
//   4. fail-soft: 1 endpoint fail KHÔNG block badge khác
//
// Usage (Core hoặc SaaS):
//   import api from '@host/axios';
//   import useSidebarBadges, { triggerSidebarBadgeRefresh } from '@cnv/ui/hooks/useSidebarBadges';
//   const counts = useSidebarBadges({
//     tasks:       { url: '/tasks/overdue-count',           pick: r => r.data?.data?.count || 0 },
//     approvals:   { url: '/approvals/inbox-counts',        pick: r => r.data?.data?.pending || 0 },
//     support:     { url: '/tickets/assigned-pending-count', pick: r => r.data?.data?.count || 0 },
//     chat:        { url: '/conversations-counts',          pick: r => Number(r.data?.data?.active) || 0 },
//     recruitment: { url: '/recruitment/pending-counts',    pick: r => r.data?.data?.pending_proposals || 0 },
//   }, { api });
//
// → { tasks: 3, approvals: 12, support: 5, chat: 8, recruitment: 2 }

import { useState, useEffect, useRef } from 'react';

// 60s — giảm tải: trước 30s. Badge không cần realtime tuyệt đối; mutate quan trọng đã có
// triggerSidebarBadgeRefresh() update ngay. Poll chỉ để bắt thay đổi từ device/user khác.
const DEFAULT_POLL_INTERVAL_MS = 60 * 1000;
export const SIDEBAR_BADGE_REFRESH_EVENT = 'sidebar-badge-refresh';

/**
 * Trigger refresh thủ công — component khác gọi sau action mutate.
 * Vd: sau approve đơn → triggerSidebarBadgeRefresh() để badge tự update.
 */
export function triggerSidebarBadgeRefresh() {
  try { window.dispatchEvent(new CustomEvent(SIDEBAR_BADGE_REFRESH_EVENT)); } catch {}
}

/**
 * @param {Object} endpointMap - { menuKey: { url, pick } }
 *   - url: API path relative (host axios sẽ prepend baseURL)
 *   - pick: (axiosResponse) => number — extract count từ response
 * @param {Object} opts
 *   - api: axios instance (REQUIRED — caller pass @host/axios)
 *   - pollIntervalMs: default 30s
 *   - debounceMs: default 500ms
 * @returns {Object} counts theo menu key — { [key]: number }
 */
export default function useSidebarBadges(endpointMap, opts = {}) {
  const { api, pollIntervalMs = DEFAULT_POLL_INTERVAL_MS, debounceMs = 500 } = opts;
  // Init counts với 0 cho mỗi key trong map.
  const initial = {};
  for (const k of Object.keys(endpointMap || {})) initial[k] = 0;
  const [counts, setCounts] = useState(initial);
  const timerRef = useRef(null);
  const lastLoadAtRef = useRef(0);
  const mapRef = useRef(endpointMap);

  // Update mapRef khi endpointMap đổi (consumer change endpoints runtime).
  mapRef.current = endpointMap;

  useEffect(() => {
    if (!api) {
      console.warn('useSidebarBadges: api instance required');
      return;
    }
    let cancelled = false;
    // Circuit breaker (2026-05-28): nếu TẤT CẢ endpoint 401 trong CONSECUTIVE
    // lần liên tiếp → STOP polling. Auth interceptor sẽ handle refresh/logout
    // riêng. Tránh badges spam log + network khi token đã expired (user feedback
    // loop "5 endpoint × poll mỗi 30s × 401 mỗi lần").
    let consecutiveAuthFail = 0;
    const MAX_CONSECUTIVE_AUTH_FAIL = 3;
    let stoppedByCircuit = false;
    // 403 = user thiếu quyền endpoint này (vd /recruitment/pending-counts khi không có
    // quyền recruitment:view). Drop endpoint khỏi vòng poll sau lần đầu — KHÔNG spam
    // request/log 403 mỗi 30s. Badge key giữ 0 (đúng — không quyền thì không badge).
    const forbiddenKeys = new Set();

    const load = async () => {
      if (stoppedByCircuit) return;
      // Pause khi tab ẩn — KHÔNG poll nền. Đa số thời gian tab ở background; bỏ poll
      // nền cắt ~70-80% request badge ở scale lớn. onVisible (visibilitychange) refetch
      // ngay khi user quay lại → vẫn thấy badge mới khi nhìn vào.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastLoadAtRef.current < debounceMs) return;                   // debounce burst events
      lastLoadAtRef.current = now;

      const map = mapRef.current || {};
      const keys = Object.keys(map).filter((k) => !forbiddenKeys.has(k));     // bỏ endpoint đã 403
      if (keys.length === 0) {
        if (timerRef.current) clearInterval(timerRef.current);                // mọi endpoint forbidden → stop poll
        return;
      }

      // DEDUP theo url — fetch mỗi UNIQUE url 1 lần (nhiều key cùng url = consolidated
      // endpoint /badges/summary → chỉ 1 request thay vì N). Backward-compat: url khác nhau
      // vẫn fetch riêng.
      const urlToKeys = {};
      for (const k of keys) {
        const u = map[k].url;
        (urlToKeys[u] = urlToKeys[u] || []).push(k);
      }
      const urls = Object.keys(urlToKeys);
      const urlSettled = await Promise.allSettled(urls.map((u) => api.get(u)));
      const urlRes = {};
      urls.forEach((u, i) => { urlRes[u] = urlSettled[i]; });

      // Map về per-key results — áp pick của từng key lên response url tương ứng.
      const results = keys.map((k) => {
        const r = urlRes[map[k].url];
        if (r.status === 'fulfilled') {
          try {
            const cfg = map[k];
            return { status: 'fulfilled', value: cfg.pick ? cfg.pick(r.value) : (r.value.data?.data?.count || 0) };
          } catch { return { status: 'fulfilled', value: 0 }; }
        }
        return { status: 'rejected', reason: r.reason };
      });

      if (cancelled) return;

      // 403 → thiếu quyền: loại endpoint khỏi vòng poll kế tiếp.
      keys.forEach((k, i) => {
        if (results[i].status === 'rejected' && results[i].reason?.response?.status === 403) {
          forbiddenKeys.add(k);
        }
      });

      // Circuit check: nếu MỌI endpoint reject 401 → token expired global.
      const all401 = results.length > 0 && results.every(r =>
        r.status === 'rejected' && r.reason?.response?.status === 401,
      );
      if (all401) {
        consecutiveAuthFail += 1;
        if (consecutiveAuthFail >= MAX_CONSECUTIVE_AUTH_FAIL) {
          stoppedByCircuit = true;
          if (timerRef.current) clearInterval(timerRef.current);
          console.warn(
            `[sidebar-badges] STOP polling — auth fail ${consecutiveAuthFail}/${MAX_CONSECUTIVE_AUTH_FAIL} lần.`,
          );
          return;
        }
      } else {
        consecutiveAuthFail = 0;       // reset khi ≥1 endpoint OK
      }

      // Merge functional — giữ count cũ cho key không poll round này (đã forbidden).
      setCounts((prev) => {
        const next = { ...prev };
        keys.forEach((k, i) => {
          const res = results[i];
          next[k] = res.status === 'fulfilled' ? (Number(res.value) || 0) : 0;
        });
        return next;
      });
    };

    load();

    // (1) Tab focus
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);

    // (2) Manual trigger event
    const onRefresh = () => load();
    window.addEventListener(SIDEBAR_BADGE_REFRESH_EVENT, onRefresh);

    // (3) Poll
    timerRef.current = setInterval(load, pollIntervalMs);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(SIDEBAR_BADGE_REFRESH_EVENT, onRefresh);
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, pollIntervalMs, debounceMs]);

  return counts;
}
